import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost, getApiError } from '../../lib/api';
import { downloadBlob, downloadBlobPost, PDF_MIME } from '../../lib/download';
import { useAuth } from '../../lib/auth';
import { Button } from '../../components/Button';
import { PageScaffold } from '../../components/PageScaffold';
import { EmailDocButton } from '../../components/EmailDocButton';
import { Modal } from '../../components/Modal';
import { DownloadReportModal } from '../../components/DownloadReportModal';
import { Money } from '../../components/Money';
import { StatusPill, paymentStatusTone } from '../../components/StatusPill';
import { FirmnessBadge } from '../../components/FirmnessBadge';
import { emptyPayment, type PaymentDraft } from '../../components/PaymentFields';
import { PaymentCapture, paymentsValid } from '../../components/PaymentCapture';
import { useHasPermission } from '../../lib/permissions';
import { OpsCalendar } from './OpsCalendar';
import { RescheduleModal, type RescheduleSubmit } from './RescheduleModal';
import { todayIsoUTC } from './opsShared';

interface DelRow {
  orderId: string;
  invoice: string;
  orderStatus: string;
  shop: string;
  customer: string;
  phone: string;
  address: string | null;
  deliveryDate: string | null;
  delivered: boolean;
  items: string;
  firmness: string | null;
  disposalQty: number;
  paymentStatus: string;
  total: string;
  balanceDue: string;
  deliveryBlockedReason: 'payment_short' | null;
}

interface SchedulingConfig {
  productionPerDay: number;
  deliveriesPerDay: number;
  productionLeadDays: number;
}

export function DeliveryReadyPage(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [err, setErr] = useState<string | null>(null);
  const [payFor, setPayFor] = useState<DelRow | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const canReschedule = useHasPermission('orders.reschedule');
  const canSeeAllShops = useHasPermission('orders.view_all_shops');

  const [modal, setModal] = useState<{ row: DelRow; date: string; error: string | null; force: boolean } | null>(null);

  const list = useQuery({
    queryKey: ['ops-delivery'],
    queryFn: () => apiGet<DelRow[]>('/operations/delivery'),
  });

  const config = useQuery({
    queryKey: ['scheduling-config'],
    queryFn: () => apiGet<SchedulingConfig>('/scheduling/config'),
  });

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ['ops-delivery'] });
  };

  const deliver = useMutation({
    mutationFn: (orderId: string) => apiPost(`/operations/delivery/${orderId}/delivered`),
    onSuccess: () => {
      setErr(null);
      invalidate();
    },
    onError: (e) => setErr(getApiError(e).message),
  });

  const pay = useMutation({
    mutationFn: (vars: { orderId: string; lines: PaymentDraft[] }) =>
      apiPost(`/operations/delivery/${vars.orderId}/pay`, {
        payments: vars.lines.map((l) => ({
          amount: Number(l.amount),
          method: l.method,
          reference: l.reference.trim() || null,
        })),
      }),
    onSuccess: () => {
      setErr(null);
      setPayFor(null);
      invalidate();
    },
  });

  const reschedule = useMutation({
    mutationFn: (vars: { row: DelRow; date: string; override?: boolean; reason?: string }) =>
      apiPatch(`/orders/${vars.row.orderId}/delivery-date`, {
        deliveryDate: vars.date,
        override: vars.override,
        reason: vars.reason,
      }),
    onSuccess: () => {
      setErr(null);
      setModal(null);
      invalidate();
    },
    onError: (e, vars) => {
      const msg = getApiError(e).message;
      setModal((cur) =>
        cur && cur.row.orderId === vars.row.orderId
          ? { ...cur, error: msg }
          : { row: vars.row, date: vars.date, error: msg, force: true },
      );
    },
  });

  // Best-effort capacity: ready-to-deliver orders per day vs the daily cap.
  // (Authoritative capacity is enforced server-side on the move.)
  const countMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of list.data ?? []) {
      if (r.deliveryDate) m.set(r.deliveryDate, (m.get(r.deliveryDate) ?? 0) + 1);
    }
    return m;
  }, [list.data]);

  // One-page route sheet: every delivery for the day on a single manifest
  // (customer, address, items, old-mattress pickup, balance to collect).
  const downloadDay = (day: string | null): void => {
    if (!day) return;
    void downloadBlob('/operations/delivery-list', { from: day, to: day, format: 'pdf' }, PDF_MIME, `delivery-route-${day}.pdf`);
  };

  // Issue + print the day's DELIVERY NOTES — one per order (priced, with a tick
  // box per item) plus a tear-off balance-receipt slip for any order still owing.
  const downloadDocs = (day: string | null): void => {
    if (!day) return;
    setErr(null);
    void downloadBlobPost('/operations/delivery-documents', { from: day, to: day }, PDF_MIME, `delivery-notes-${day}.pdf`).catch(
      (e) => setErr(getApiError(e).message),
    );
  };

  const openReschedule = (row: DelRow): void => {
    if (!canReschedule || row.delivered) return;
    setModal({ row, date: row.deliveryDate ?? todayIsoUTC(), error: null, force: false });
  };

  const submitModal = (args: RescheduleSubmit): void => {
    if (!modal) return;
    reschedule.mutate({ row: modal.row, date: args.date, override: args.override, reason: args.reason });
  };

  return (
    <PageScaffold
      title="Delivery"
      actions={
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" className="shrink-0" onClick={() => setShowReportModal(true)}>
            Download report · Excel
          </Button>
          <Button variant="primary" size="sm" className="shrink-0" onClick={() => downloadDocs(todayIsoUTC())}>
            Print today's deliveries · PDF
          </Button>
        </div>
      }
    >

      {err && <div className="rounded border border-negative/30 bg-negative-soft text-negative text-sm px-4 py-2">{err}</div>}

      {list.isLoading ? (
        <div className="text-center text-ink-3 py-10">Loading…</div>
      ) : (
        <OpsCalendar<DelRow>
          rows={list.data ?? []}
          getDay={(r) => r.deliveryDate}
          getId={(r) => r.orderId}
          canMove={(r) => canReschedule && !r.delivered}
          onMove={(row, targetIso) => reschedule.mutate({ row, date: targetIso })}
          onClickItem={openReschedule}
          capacityFor={(iso) => (config.data ? { used: countMap.get(iso) ?? 0, cap: config.data.deliveriesPerDay } : null)}
          emptyLabel="Nothing ready to deliver on this day."
          dayHeaderAction={(iso) => (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => downloadDocs(iso)}>
                Delivery notes · PDF
              </Button>
              <Button variant="ghost" size="sm" onClick={() => downloadDay(iso)}>
                Route sheet · PDF
              </Button>
              <EmailDocButton
                endpoint="/operations/delivery-list"
                method="get"
                params={{ from: iso, to: iso, format: 'pdf' }}
                selfDefault
                buttonVariant="ghost"
                buttonSize="sm"
                buttonLabel="Email route sheet"
                title="Email delivery route sheet"
                subtitle="Send the driver's one-page route sheet (PDF) by email."
              />
            </div>
          )}
          renderChip={(r) => {
            const blocked = r.deliveryBlockedReason === 'payment_short';
            return (
              <div className="space-y-0.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="kc-mono text-[11px] text-ink-3">{r.invoice}</span>
                  {r.delivered ? (
                    <StatusPill tone="positive">delivered ✓</StatusPill>
                  ) : (
                    <StatusPill tone={paymentStatusTone(r.paymentStatus)}>{r.paymentStatus}</StatusPill>
                  )}
                </div>
                <div className="text-ink font-medium leading-tight">{r.customer}</div>
                {r.address && <div className="text-[11px] text-ink-3">{r.address}</div>}
                <div className="text-[11px] text-ink-3 line-clamp-2">{r.items}</div>
                <FirmnessBadge value={r.firmness} />
                {r.disposalQty > 0 && <div className="text-[11px] text-warning">collect {r.disposalQty} old</div>}
                {!r.delivered && Number(r.balanceDue) > 0 && (
                  <div className="text-[11px] text-negative">
                    Balance <Money value={Number(r.balanceDue)} />
                  </div>
                )}
                {blocked && <div className="text-[11px] text-negative">deposit not verified</div>}
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/orders/${r.orderId}`);
                    }}
                    className="text-[11px] text-ink-3 hover:text-accent hover:underline"
                  >
                    Open order
                  </button>
                  {!r.delivered && Number(r.balanceDue) > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        pay.reset();
                        setPayFor(r);
                      }}
                      className="text-[11px] text-accent hover:underline"
                    >
                      Paid
                    </button>
                  )}
                  {!r.delivered && (
                    <button
                      type="button"
                      disabled={blocked || (deliver.isPending && deliver.variables === r.orderId)}
                      title={blocked ? 'Record the deposit/COD payment first' : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        deliver.mutate(r.orderId);
                      }}
                      className="text-[11px] text-positive hover:underline disabled:opacity-40 disabled:no-underline"
                    >
                      Delivered ✓
                    </button>
                  )}
                </div>
              </div>
            );
          }}
        />
      )}

      {payFor && (
        <PayModal
          row={payFor}
          busy={pay.isPending}
          error={pay.isError ? getApiError(pay.error).message : null}
          onClose={() => setPayFor(null)}
          onSubmit={(lines) => pay.mutate({ orderId: payFor.orderId, lines })}
        />
      )}

      {modal && (
        <RescheduleModal
          title="Reschedule delivery"
          subtitle={`${modal.row.invoice} · ${modal.row.customer}`}
          initialDate={modal.date}
          minDate={todayIsoUTC()}
          busy={reschedule.isPending}
          error={modal.error}
          forceOverride={modal.force}
          onClose={() => setModal(null)}
          onSubmit={submitModal}
        />
      )}

      {showReportModal && (
        <DownloadReportModal
          title="Download delivery report"
          endpoint="/operations/delivery-list"
          extraParams={{ format: 'excel' }}
          filenamePrefix="delivery-report"
          showShop={canSeeAllShops}
          defaultFrom={todayIsoUTC()}
          defaultTo={todayIsoUTC()}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </PageScaffold>
  );
}

// The same record-payment capture as the order page (method tiles, quick-fills,
// mixed/split tender), in a modal. Posts to the delivery endpoint so cash keeps
// its COD routing (Cash-in-Transit until the driver remits).
function PayModal({
  row,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  row: DelRow;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (lines: PaymentDraft[]) => void;
}): JSX.Element {
  const { user, perms } = useAuth();
  const balance = Number(row.balanceDue);
  const [lines, setLines] = useState<PaymentDraft[]>(() => [emptyPayment(balance.toFixed(2))]);

  return (
    <Modal title="Record payment" size="lg" subtitle={`Invoice ${row.invoice} · ${row.customer}`} onClose={onClose}>
      <p className="text-xs text-ink-3 mb-4">
        The balance the customer paid at the door — pick the mode they actually paid. Cash routes to Cash-in-Transit until the
        driver remits to HQ; other modes post to their own account.
      </p>
      <PaymentCapture
        lines={lines}
        onChange={setLines}
        total={balance}
        userName={user?.name}
        shopName={row.shop}
        canRecordCheque={perms.has('payments.record_cheque')}
      />
      {error && <div className="text-xs text-negative mt-3">{error}</div>}
      <div className="flex gap-2 mt-5">
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          Cancel
        </Button>
        <Button className="flex-1" loading={busy} disabled={!paymentsValid(lines, balance)} onClick={() => onSubmit(lines)}>
          Record payment
        </Button>
      </div>
    </Modal>
  );
}
